import React, { useState, useRef, useEffect } from "react";
import {
  Typography,
  Button,
  IconButton,
  Box,
  Stack,
  Paper,
  alpha,
  Alert,
  AlertTitle,
  Tooltip
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import DeleteIcon from "@mui/icons-material/Delete";
import UndoIcon from "@mui/icons-material/Undo";
import { baseURLFetch } from "../../../utils/ConstantsURL"
import { AudioMarkers, formatTime } from "./AudioMarkers";

export const AudioUpload = ({
  onUpload,
  onDelete,
  loading,
  existingAudio,
  markers = [],
  onMarkersChange,
  docId,
  moduleKey,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const inputRef = useRef(null);
  const audioRef = useRef(null);

  const handleSeek = (seconds) => {
    if (!audioRef.current) return;

    const target = Math.max(0, Math.min(seconds, audioDuration || seconds));

    audioRef.current.currentTime = target;
    setCurrentTime(target);
  };

  const handleTimelineClick = (event) => {
    if (!audioDuration) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = (event.clientX - rect.left) / rect.width;

    handleSeek(ratio * audioDuration);
  };

  useEffect(() => {
    if (loading) return;

    if (existingAudio?.url) {
      const fullAudioUrl = existingAudio.url.startsWith("http")
        ? existingAudio.url
        : `${baseURLFetch}${existingAudio.url}`;

      setSelectedFile((prev) => {
        if (prev && !prev.isExisting) {
          return prev;
        }

        return {
          name: existingAudio.nombre || "audio_guardado.m4a",
          size: existingAudio.size || 0,
          isExisting: true,
        };
      });

      setPreviewUrl((prev) => {
        if (prev?.startsWith("blob:")) {
          return prev;
        }

        return fullAudioUrl;
      });

    } else {
      setSelectedFile((prev) =>
        prev && !prev.isExisting ? prev : null
      );

      setPreviewUrl((prev) =>
        prev?.startsWith("blob:") ? prev : null
      );
    }

  }, [existingAudio, loading]);

  const handleFile = (file) => {
    if (file && file.type.startsWith("audio/")) {

      if (previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }

      file.isExisting = false;
      setSelectedFile(file);

      setPreviewUrl(URL.createObjectURL(file));
    } else {
      alert("Por favor, selecciona un archivo de audio válido.");
    }
  };

  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    if (selectedFile && !selectedFile.isExisting && existingAudio?.url) {

      const fullAudioUrl = existingAudio.url.startsWith("http")
        ? existingAudio.url
        : `${baseURLFetch}${existingAudio.url}`;

      setSelectedFile({
        name: existingAudio.nombre || "audio_guardado.m4a",
        size: existingAudio.size || 0,
        isExisting: true,
      });

      setPreviewUrl(fullAudioUrl);
    } else {
      if (selectedFile?.isExisting && onDelete) {
        onDelete();
      }

      setSelectedFile(null);
      setPreviewUrl(null);

      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, sm: 4 },
        textAlign: "center",
        borderRadius: 3,
        width: "100%",
        border: existingAudio && !selectedFile?.isExisting ? "2px solid #2563eb" : "1px solid #e2e8f0"
      }}
    >
      {loading && (
        <Alert severity="info" sx={{ mb: 3, textAlign: 'left', borderRadius: 2 }}>
          <AlertTitle>Módulo bloqueado</AlertTitle>
          No se pueden cargar más audios hasta que <strong>guardes los cambios actuales</strong> (botón Vista previa). 
          Esto asegura que el archivo se vincule correctamente al documento seleccionado.
        </Alert>
      )}

      <Typography
        variant="h6"
        gutterBottom
        sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}
      >
        {selectedFile?.isExisting ? "Audio Actual" : "Gestión de Audio"}
      </Typography>

      <Box
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
        onClick={() => !loading && inputRef.current.click()}
        sx={{
          border: "2px dashed",
          borderColor: dragActive ? "#2563eb" : "#cbd5e1",
          borderRadius: 2,
          p: { xs: 3, sm: 6 },
          backgroundColor: dragActive ? alpha("#2563eb", 0.05) : "#f8fafc",
          cursor: loading ? "not-allowed" : "pointer",
          opacity: loading ? 0.6 : 1,
          transition: "all 0.2s",
          "&:hover": {
            borderColor: "#2563eb",
            backgroundColor: alpha("#2563eb", 0.02)
          }
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="audio/*"
          style={{ display: "none" }}
          disabled={loading}
          onChange={(e) => handleFile(e.target.files[0])}
        />

        {!selectedFile ? (
          <Stack alignItems="center" spacing={2}>
            <CloudUploadIcon sx={{ fontSize: { xs: 40, sm: 48 }, color: "#64748b" }} />
            <Typography
              variant="body1"
              color="textSecondary"
              sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
            >
              Arrastra un audio aquí o haz clic para buscar
            </Typography>
          </Stack>
        ) : (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems="center"
            justifyContent="center"
            spacing={{ xs: 2, sm: 3 }}
          >
            <AudioFileIcon color={selectedFile.isExisting ? "success" : "primary"} sx={{ fontSize: { xs: 32, sm: 40 } }} />
            <Box sx={{ textAlign: { xs: "center", sm: "left" }, maxWidth: "100%", overflow: "hidden" }}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  wordBreak: "break-all"
                }}
              >
                {selectedFile.name}
              </Typography>
              <Typography variant="caption" display="block">
                {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                {selectedFile.isExisting && " (Guardado)"}
              </Typography>
            </Box>
            <IconButton
              color={!selectedFile.isExisting && existingAudio?.url ? "warning" : "error"}
              onClick={handleRemove}
              disabled={loading}
              title={!selectedFile.isExisting && existingAudio?.url ? "Descartar cambios" : "Quitar audio"}
            >
              {!selectedFile.isExisting && existingAudio?.url ? <UndoIcon /> : <DeleteIcon />}
            </IconButton>
          </Stack>
        )}
      </Box>

      {previewUrl && (
        <Box sx={{ mt: 4 }}>
          <audio
            key={previewUrl}
            ref={audioRef}
            controls
            src={previewUrl}
            style={{ width: "100%", borderRadius: "8px" }}
            onLoadedMetadata={(e) => {
              setAudioDuration(
                Number.isFinite(e.target.duration) ? e.target.duration : 0
              );
              setCurrentTime(0);
            }}
            onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
            onSeeked={(e) => setCurrentTime(e.target.currentTime)}
          />

          <Box sx={{ mt: 1 }}>
            <Tooltip title="Línea de tiempo del audio: los bloques indican los segundos que ya tienen marcador. Haz clic para moverte.">
              <Box
                onClick={handleTimelineClick}
                sx={{
                  position: "relative",
                  height: 18,
                  borderRadius: 1,
                  backgroundColor: "#e2e8f0",
                  cursor: audioDuration ? "pointer" : "default",
                  overflow: "hidden",
                }}
              >
                {audioDuration > 0 &&
                  markers.map((marker) => (
                    <Box
                      key={marker.id}
                      title={`${formatTime(marker.inicio)} → ${formatTime(
                        marker.inicio + marker.duracion
                      )}`}
                      sx={{
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        left: `${Math.min(
                          100,
                          (marker.inicio / audioDuration) * 100
                        )}%`,
                        width: `${Math.max(
                          0.8,
                          Math.min(
                            100,
                            (marker.duracion / audioDuration) * 100
                          )
                        )}%`,
                        backgroundColor: alpha("#f59e0b", 0.85),
                        borderLeft: "2px solid #b45309",
                      }}
                    />
                  ))}

                {audioDuration > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      left: `${Math.min(
                        100,
                        (currentTime / audioDuration) * 100
                      )}%`,
                      width: "2px",
                      backgroundColor: "#2563eb",
                    }}
                  />
                )}
              </Box>
            </Tooltip>

            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ mt: 0.5 }}
            >
              <Typography variant="caption" color="text.secondary">
                {formatTime(currentTime)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatTime(audioDuration)}
              </Typography>
            </Stack>
          </Box>

          {!selectedFile?.isExisting && (
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => onUpload(selectedFile)}
              disabled={loading}
              sx={{
                mt: 3,
                py: 1.5,
                background: "linear-gradient(95deg,#2563eb,#1d4ed8)",
                fontSize: { xs: "0.9rem", sm: "1rem" }
              }}
            >
              {loading ? "Subiendo..." : "Confirmar y Reemplazar Audio"}
            </Button>
          )}

          {onMarkersChange && (
            <AudioMarkers
              markers={markers}
              onChange={onMarkersChange}
              docId={docId}
              moduleKey={moduleKey}
              currentTime={currentTime}
              audioDuration={audioDuration}
              onSeek={handleSeek}
              disabled={loading}
            />
          )}
        </Box>
      )}
    </Paper>
  );
};